import React, { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import html2canvas from 'html2canvas';
import {
    decorateCapture,
    patchUnsupportedColors,
    DecoratedCaptureResult,
} from '@/components/Hypertracker/HypertrackerCapture/captureHelpers';
import hypertrackerLogo from '@/assets/images/hypertracker/hypertracker_logo.png';
import CaptureResultModal from '@/components/Hypertracker/HypertrackerCapture/CaptureResultModal';
import { useLocation } from 'react-router';
import { useMediaQuery, useTheme } from '@mui/material';

type CaptureContextValue = {
    isActive: boolean;
    selectedCount: number;
    start(): void;
    stop(): void;
    capture(): Promise<DecoratedCaptureResult | null>;
    registerContainer: React.RefCallback<HTMLElement>;
    registerBanner: React.RefCallback<HTMLElement>;
};

export const CaptureContext = createContext<CaptureContextValue>({
    isActive: false,
    selectedCount: 0,
    start: () => {},
    stop: () => {},
    capture: async () => null,
    registerContainer: () => {},
    registerBanner: () => {},
});

const DEFAULT_AUTO_CAPTUREABLE_SELECTORS = ['.captureable-row', '.MuiDataGrid-row'];
const SELECTED_CAPTUREABLE_SELECTOR =
    '[data-captureable].is-selected, .MuiDataGrid-row.is-selected';

const cleanupCaptureRoot = (root: HTMLElement | null) => {
    if (!root) return;

    root.querySelectorAll<HTMLElement>(SELECTED_CAPTUREABLE_SELECTOR).forEach((el) => {
        el.classList.remove('is_selected');
        el.classList.remove('is-selected');
    });

    root.classList.remove('cmm-capture-active', 'cmm-capture-mode', 'cmm-dim-others');
};

type Props = {
    children: React.ReactNode;
    containerSelector: string;
    autoCaptureableSelectors?: string[];
    backgroundColor?: string | null;
    scale?: number;
};

export function CaptureProvider({
    children,
    containerSelector,
    autoCaptureableSelectors = DEFAULT_AUTO_CAPTUREABLE_SELECTORS,
    backgroundColor = null,
    scale,
}: Props) {
    const [isActive, setIsActive] = useState(false);
    const [banner, setBanner] = useState<HTMLElement | null>(null);
    const [container, setContainer] = useState<HTMLElement | null>(null);
    const [selectedCount, setSelectedCount] = useState(0);
    const [resultImage, setResultImage] = useState<DecoratedCaptureResult | null>(null);
    const location = useLocation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const containerRef = useRef<HTMLElement | null>(null);
    const registeredContainerRef = useRef(false);
    const startDelayRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const selectedRef = useRef<Set<HTMLElement>>(new Set());

    const autoCaptureableSelectorsKey = autoCaptureableSelectors.join('|');

    const clearStartDelay = useCallback(() => {
        if (!startDelayRef.current) return;

        clearTimeout(startDelayRef.current);
        startDelayRef.current = null;
    }, []);

    const setCaptureContainer = useCallback(
        (el: HTMLElement | null) => {
            const previous = containerRef.current;
            if (previous === el) return;

            clearStartDelay();
            cleanupCaptureRoot(previous);
            selectedRef.current.clear();
            setSelectedCount(0);
            if (!el) setIsActive(false);

            containerRef.current = el;
            setContainer(el);
        },
        [clearStartDelay],
    );

    const registerContainer = useCallback(
        (el: HTMLElement | null) => {
            registeredContainerRef.current = !!el;
            setCaptureContainer(el);
        },
        [setCaptureContainer],
    );

    useEffect(() => {
        if (registeredContainerRef.current) return;

        let fallbackContainer: HTMLElement | null = null;
        const frameId = requestAnimationFrame(() => {
            if (registeredContainerRef.current) return;

            fallbackContainer = document.querySelector(containerSelector) as HTMLElement | null;
            setCaptureContainer(fallbackContainer);
        });

        return () => {
            cancelAnimationFrame(frameId);
            if (!registeredContainerRef.current && containerRef.current === fallbackContainer) {
                setCaptureContainer(null);
            }
        };
    }, [containerSelector, location.hash, location.pathname, location.search, setCaptureContainer]);

    useEffect(() => {
        if (!container) return;

        const markCaptureables = (root: ParentNode) => {
            autoCaptureableSelectors.forEach((sel) => {
                if (root instanceof HTMLElement && root.matches(sel)) {
                    root.setAttribute('data-captureable', 'true');
                }

                root.querySelectorAll<HTMLElement>(sel).forEach((el) =>
                    el.setAttribute('data-captureable', 'true'),
                );
            });
        };

        markCaptureables(container);

        const mo = new MutationObserver((mutations) => {
            mutations.forEach((m) => {
                m.addedNodes.forEach((n) => {
                    if (n instanceof HTMLElement) markCaptureables(n);
                });
            });
        });
        mo.observe(container, { childList: true, subtree: true });
        return () => mo.disconnect();
    }, [container, isActive, autoCaptureableSelectors, autoCaptureableSelectorsKey]);

    const toggleSelection = useCallback((el: HTMLElement) => {
        const set = selectedRef.current;

        if (set.has(el)) {
            set.delete(el);
            el.classList.remove('is-selected');
        } else {
            set.add(el);
            el.classList.add('is-selected');
        }

        const root = containerRef.current;
        if (root) root.classList.toggle('cmm-dim-others', set.size > 0);

        setSelectedCount(set.size);
    }, []);

    const onClick = useCallback(
        (e: MouseEvent) => {
            const root = containerRef.current;
            if (!root) return;

            const el = (e.target as HTMLElement | null)?.closest(
                '[data-captureable]',
            ) as HTMLElement | null;

            if (!el || !root.contains(el)) return;

            e.preventDefault();
            e.stopPropagation();

            toggleSelection(el);
        },
        [toggleSelection],
    );

    const start = useCallback(() => {
        const root = containerRef.current;
        if (!root) {
            console.warn('No container found');
            return;
        }

        setIsActive(true);
        clearStartDelay();
        startDelayRef.current = setTimeout(() => {
            if (containerRef.current === root) {
                root.classList.add('cmm-capture-active', 'cmm-capture-mode');
            }
            startDelayRef.current = null;
        }, 200);
    }, [clearStartDelay]);

    const stop = useCallback(() => {
        const root = containerRef.current;
        clearStartDelay();
        setIsActive(false);

        cleanupCaptureRoot(root);

        selectedRef.current.clear();
        setSelectedCount(0);
    }, [clearStartDelay]);

    useEffect(() => {
        if (!isActive) return;

        window.addEventListener('click', onClick, true);
        return () => window.removeEventListener('click', onClick, true);
    }, [isActive, onClick]);

    const registerBanner = useCallback((el: HTMLElement | null) => {
        setBanner(el);
    }, []);

    const capture = useCallback(async (): Promise<DecoratedCaptureResult | null> => {
        try {
            const root = containerRef.current;
            if (!root) return null;
            if (selectedCount === 0) root.classList.remove('cmm-dim-others');

            const pxScale = scale ?? Math.max(2, window.devicePixelRatio || 1);
            await new Promise(requestAnimationFrame);

            const rect = root.getBoundingClientRect();
            const viewportTop = 0;
            const viewportBottom = window.innerHeight;

            const visibleTop = Math.max(rect.top, viewportTop);
            const visibleBottom = Math.min(rect.bottom, viewportBottom);
            const visibleHeight = Math.max(0, visibleBottom - visibleTop);

            if (visibleHeight <= 0) {
                console.warn('Nothing visible to capture');
                return null;
            }

            const visibleWidth = rect.width;

            const restoreColors = patchUnsupportedColors(root);
            let canvas: HTMLCanvasElement;
            try {
                canvas = await html2canvas(root, {
                    useCORS: true,
                    allowTaint: false,
                    backgroundColor: backgroundColor ?? null,
                    x: 0,
                    y: Math.round(window.scrollY),
                    width: Math.round(visibleWidth),
                    height: Math.round(visibleHeight),
                    scrollX: 0,
                    scrollY: -window.scrollY,
                    scale: pxScale,
                    removeContainer: true,
                    ignoreElements: (el) =>
                        el === banner ||
                        (el instanceof HTMLElement &&
                            el.getAttribute('data-html2canvas-ignore') === 'true'),
                });
            } finally {
                restoreColors();
            }

            const result = await decorateCapture(canvas, hypertrackerLogo, isMobile);
            setResultImage(result);
            return result;
        } catch (error) {
            console.error('Capture failed:', error);
            return null;
        }
    }, [banner, backgroundColor, scale, selectedCount, isMobile]);

    const value = useMemo<CaptureContextValue>(
        () => ({
            isActive,
            selectedCount,
            start,
            stop,
            capture,
            registerContainer,
            registerBanner,
        }),
        [isActive, selectedCount, start, stop, capture, registerContainer, registerBanner],
    );

    return (
        <CaptureContext.Provider value={value}>
            {children}
            <CaptureResultModal open={resultImage} stop={stop} setResultImage={setResultImage} />
        </CaptureContext.Provider>
    );
}

export const useCapture = () => React.useContext(CaptureContext);
